import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE } from "@/data/site";

const NAV = [
    { id: "historia", label: "Nossa História" },
    { id: "acoes", label: "Nossas Ações" },
    { id: "projeto", label: "Projeto Meninas", route: "/projeto-meninas-vencedoras" },
    { id: "galeria", label: "Galeria" },
    { id: "membro", label: "Seja Membro" },
    { id: "contato", label: "Contato" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goTo = (item) => {
        setOpen(false);
        if (item.route) {
            navigate(item.route);
            return;
        }
        // scroll to section — if not on home, navigate first
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => {
                document
                    .getElementById(item.id)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
        } else {
            document
                .getElementById(item.id)
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const goHome = () => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    return (
        <motion.header
            data-testid="site-header"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                scrolled
                    ? "bg-[#051126]/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent"
            }`}
        >
            <div className="container-x flex items-center justify-between py-5">
                <a
                    href="/"
                    onClick={(e) => {
                        e.preventDefault();
                        goHome();
                    }}
                    data-testid="header-brand"
                    className="group flex items-center gap-3"
                >
                    <span className="inline-flex h-11 w-11 items-center justify-center">
                        <img
                            src="/logo.png"
                            alt="Logo Novo Horizonte F.C."
                            className="h-full w-full object-contain"
                        />
                    </span>
                    <span className="hidden sm:flex flex-col leading-none">
                        <span className="font-display text-[15px] tracking-[0.14em] text-white">
                            {SITE.brand}
                        </span>
                        <span className="font-body text-[10px] tracking-[0.32em] text-white/50 mt-1">
                            AENHCC · {SITE.since}
                        </span>
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-7">
                    {NAV.map((n) => (
                        <button
                            key={n.id}
                            data-testid={`nav-link-${n.id}`}
                            onClick={() => goTo(n)}
                            className={`link-sweep font-body text-[12px] tracking-[0.22em] uppercase hover:text-white ${
                                n.route && location.pathname === n.route
                                    ? "text-[var(--sun)]"
                                    : "text-white/85"
                            }`}
                        >
                            {n.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => goTo({ id: "membro" })}
                        data-testid="header-cta-membro"
                        className="hidden md:inline-flex btn-brutal py-2.5 px-4 text-[11px]"
                    >
                        Seja Membro
                    </button>
                    <button
                        aria-label="Abrir menu"
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex h-10 w-10 items-center justify-center border border-white/20 text-white"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                            duration: 0.35,
                            ease: [0.76, 0, 0.24, 1],
                        }}
                        className="lg:hidden overflow-hidden border-t border-white/10 bg-[#051126]"
                    >
                        <div className="container-x py-6 flex flex-col gap-4">
                            {NAV.map((n) => (
                                <button
                                    key={n.id}
                                    onClick={() => goTo(n)}
                                    data-testid={`mobile-nav-${n.id}`}
                                    className="text-left font-display text-2xl text-white/90 hover:text-[var(--sun)]"
                                >
                                    {n.label}
                                </button>
                            ))}
                            <button
                                onClick={() => goTo({ id: "membro" })}
                                data-testid="mobile-cta-membro"
                                className="btn-brutal mt-4 justify-center"
                            >
                                Seja Membro
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
